import React from 'react'
import classNames from 'classnames'

import { CTALink } from '../../elements/CTALink'
import { ImageOrAutoplayVideo } from '../../elements/ImageOrAutoplayVideo'
import { NewSticker } from '../../elements/NewSticker'
import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import {
  ColorName as themeNames,
  ColorTheme,
  AllSystemColors,
  getColorTheme,
  AllColorValues,
  getColor,
  mq,
  CTATheme,
} from '../../utils'
import { AllColors as BrandColors } from '../../utils/constants/themes'
import { IProps } from './index'

interface ILocalTheme extends ColorTheme {
  stickerTheme: AllSystemColors
}

function getLocalTheme(bgColor: AllSystemColors): ILocalTheme {
  const baseColors = getColorTheme('default', bgColor)
  switch (baseColors.id) {
    case AllColorValues.LegacyTeal:
      return {
        ...baseColors,
        stickerTheme: getColor('default', AllColorValues.Sky),
      }
    case AllColorValues.LegacyBlack:
      return {
        ...baseColors,
        CTATheme: CTATheme.TealFill,
        stickerTheme: getColor('default', AllColorValues.Aqua),
      }
    default:
      return {
        ...baseColors,
        stickerTheme: getColor('default', AllColorValues.Aqua),
      }
  }
}

export const VerticalFeatureSpotlight = ({
  newSticker,
  eyebrow,
  heading,
  subheading,
  imageRef,
  mobileImage,
  bgColor,
  cta,
  arrowOnRight = false,
  video,
  videoWebm,
  wide,
  imageOnTop,
  topTexture,
}: IProps): React.ReactElement => {
  const themeColors = getLocalTheme(bgColor)
  const compatibleColor = getColor('default', bgColor)
  const spacing = useSpacing()

  const containerClasses = classNames(
    'feature-spotlight-vertical',
    `feature-spotlight-vertical--theme theme-${themeNames[compatibleColor as BrandColors]}`,
    {
      'feature-spotlight-vertical--image-on-top': imageOnTop,
      'feature-spotlight-vertical--wide': wide,
      'feature-spotlight-vertical--top-texture': topTexture,
      'feature-spotlight-vertical--padding': !spacing,
    },
  )

  return (
    <div className={containerClasses}>
      {!wide && !arrowOnRight && (
        <div className="feature-spotlight-vertical__left-gutter">
          <div className="feature-spotlight-vertical__bg-arrow" />
        </div>
      )}
      <div className="feature-spotlight-vertical__content">
        {newSticker && (
          <div className="feature-spotlight-vertical__new-sticker">
            <NewSticker theme={themeColors.stickerTheme} />
          </div>
        )}
        {eyebrow && (
          <span className="feature-spotlight-vertical__eyebrow">
            <Text brand="default" size="eyebrows">
              {eyebrow}
            </Text>
          </span>
        )}
        {heading && (
          <p className="feature-spotlight-vertical__heading">
            <Text brand="default" size="heading-2">
              {heading}
            </Text>
          </p>
        )}
        {subheading && (
          <div className="feature-spotlight-vertical__subheading">
            <Text brand="default" size="heading-4">
              <RichText html={subheading} />
            </Text>
          </div>
        )}
        {cta && (
          <div className="feature-spotlight-vertical__cta">
            <CTALink {...cta} bgColor={cta.bgColor || themeColors.CTATheme} arrow />
          </div>
        )}
      </div>

      <div className="feature-spotlight-vertical__lower-content">
        <ImageOrAutoplayVideo
          imageRef={imageRef}
          video={video}
          videoWebm={videoWebm}
          mobileImage={mobileImage}
        />
      </div>

      {!wide && arrowOnRight && (
        <div className="feature-spotlight-vertical__right-gutter">
          <div className="feature-spotlight-vertical__bg-arrow  feature-spotlight-vertical__bg-arrow--flipped-vertical" />
        </div>
      )}

      <style jsx global>{`
        .feature-spotlight-vertical {
          display: grid;
          grid-template-columns: [left-gutter] 2fr [content] 10fr [right-gutter] 2fr;
          grid-template-rows: repeat(2, auto);
          text-align: center;
          padding-left: 0;
          padding-right: 0;
          position: relative;

          &--padding {
            padding-top: 40px;
            padding-bottom: 40px;
          }

          &--theme {
            background-color: ${themeColors.backgroundColor};
            color: ${themeColors.textColor};
          }
        }

        .feature-spotlight-vertical__left-gutter {
          display: grid;
          grid-column: left-gutter;
          place-content: start;
          grid-row: 1 / -1;
        }

        .feature-spotlight-vertical--wide {
          .feature-spotlight-vertical__left-gutter {
            grid-row: 1;
          }

          .feature-spotlight-vertical__right-gutter {
            grid-row: 1;
          }

          .feature-spotlight-vertical__lower-content {
            grid-column: 1 / -1;
            padding: 0 40px;
          }
        }

        .feature-spotlight-vertical__content {
          display: grid;
          grid-column: content;
          grid-row: 1;
          margin: 0;
          margin-bottom: 56px;
          margin-top: 0;
        }

        .feature-spotlight-vertical--image-on-top {
          .feature-spotlight-vertical__left-gutter {
            grid-row: 2;
            margin-bottom: 0;
            margin-top: 56px;
          }

          .feature-spotlight-vertical__lower-content {
            grid-row: 1;
          }

          .feature-spotlight-vertical__content {
            grid-row: 2;
          }

          .feature-spotlight-vertical__subheading {
            order: 3;
          }
        }

        .feature-spotlight-vertical__right-gutter {
          display: grid;
          grid-column: right-gutter;
          place-content: end;
          grid-row: 1 / -1;
        }

        .feature-spotlight-vertical__bg-arrow--flipped-vertical {
          transform: rotate(180deg);
        }

        .feature-spotlight-vertical__new-sticker {
          margin-bottom: 16px;
        }

        .feature-spotlight-vertical__eyebrow {
          display: block;
          margin-bottom: 20px;
          word-break: break-word;
        }

        .feature-spotlight-vertical__subheading {
          margin-top: 20px;
          justify-self: center;
        }

        .feature-spotlight-vertical__subheading ul {
          text-align: left;
        }

        .feature-spotlight-vertical__cta {
          margin-top: 36px;
        }

        .feature-spotlight-vertical__lower-content {
          grid-row: 2;
          grid-column: 2;
          padding: 0;

          & > picture > img,
          video {
            border-radius: 25px;
          }
        }

        @media (${mq.tablet}) {
          .feature-spotlight-vertical {
            display: grid;
            grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr;
            text-align: center;
            padding-left: 0;
            padding-right: 0;

            &--padding {
              padding-top: 80px;
              padding-bottom: 80px;
            }
          }

          .feature-spotlight-vertical.feature-spotlight-vertical--image-on-top {
            .feature-spotlight-vertical__content {
              margin-bottom: 0;
              margin-top: 80px;
            }
          }

          .feature-spotlight-vertical.feature-spotlight-vertical--wide {
            .feature-spotlight-vertical__lower-content {
              padding: 3vw 12vw;
            }
          }

          .feature-spotlight-vertical__content {
            margin: 0 auto;
            margin-bottom: 80px;
            margin-top: 0;
            max-width: 64.25rem;
            text-align: center;
          }

          .feature-spotlight-vertical__lower-content {
            padding: 0;
            max-width: 1440px;
            margin: 0 auto;
          }

          .feature-spotlight-vertical__lower-content picture,
          .feature-spotlight-vertical__lower-content picture img {
            max-height: 600px;
            max-width: 100%;
            width: auto;
          }
        }
      `}</style>
    </div>
  )
}
